import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GiveModalConfirmDelete } from '@bonusly/common-give-box';
import {
	Box,
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	RefreshOutline,
	rem,
	Skeleton,
	SparklesSolid,
	Switch,
	Tag,
	Text,
} from '@bonusly/design-system';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { CustomVideoPlayer } from '../../video/custom-video-player';
import { VideoModalProps } from './bonus-video-recording-modal';
import { CountdownOverlay } from './components/CountdownOverlay';
import { DeviceSettings } from './components/DeviceSettings';
import { MobileResetVideoButton } from './components/MobileResetVideoButton';
import { RecordButton } from './components/RecordButton';
import { RemainingTime } from './components/RemainingTime';
import { StopButton } from './components/StopButton';
import { getVideoModalText } from './utils/get-video-modal-text';

export const BonusVideoRecordingModalMobile: FC<VideoModalProps> = ({
	isOpen,
	onClose,
	videoRef,
	recordedVideoRef,
	isRecording,
	recordedVideo,
	recordedPreviewReady,
	videoPoster,
	onStartRecording,
	onStopRecording,
	onRestartRecording,
	onSubmit,
	countdown,
	formattedTime,
	totalTime,
	timerColor,
	devices,
	handleDeviceChange,
	selectedVideoDeviceRef,
	selectedAudioDeviceRef,
	contextType,
	obtainedDevicePermissionRef,
	confirmModalIsOpen,
	confirmModalOnClose,
	onConfirmModalClose,
	getVideoFilename,
	isLoadingSuggestedReason,
	aiProcessingEnabled,
	onToggleAiProcessing,
}) => {
	const { t } = useTranslation('video');
	const { heading, submitButton, restartButton } = getVideoModalText(contextType, t);
	const showRecordButton = !recordedVideo && !isRecording && countdown === null && obtainedDevicePermissionRef.current;
	const showLoadingSkeleton = !showRecordButton && countdown === null && !isRecording && !recordedPreviewReady;
	const videoFinishedRecording = !showRecordButton && !showLoadingSkeleton && !isRecording && countdown === null && recordedPreviewReady;
	const videoStreamActive = !videoFinishedRecording;
	const mobileVideoFeatureFlag = useFeatureFlag('mobile-video');

	const renderRecordedVideoHeader = () => {
		return (
			<ModalHeader display="flex" alignItems="center" justifyContent="space-between" px={4} py={3} borderBottom="1px solid" borderColor="border.default">
				<Text fontSize="lg" fontWeight="semibold">
					{heading}
				</Text>
				<ModalCloseButton position="static" />
			</ModalHeader>
		);
	};

	const renderPreviewHeader = () => {
		if (isRecording) {
			return (
				<Box position="absolute" top={4} left="50%" transform="translateX(-50%)" zIndex={1}>
					<RemainingTime formattedTime={formattedTime} totalTime={totalTime} timerColor={timerColor} />
				</Box>
			);
		} else if (showRecordButton) {
			return (
				<Box position="absolute" top={0} left={0} zIndex={1} p={4}>
					<ModalCloseButton position="static" color="white" bg="rgba(0, 0, 0, 0.3)" />
				</Box>
			);
		} else {
			return null;
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
			<ModalOverlay bg="white" />
			<ModalContent
				margin={0}
				height="100vh"
				bg="white"
				data-testid="mobile-video-modal"
				display="flex"
				flexDirection="column"
				pt="env(safe-area-inset-top)"
				sx={{
					'@supports (-webkit-touch-callout: none)': {
						height: '-webkit-fill-available',
						minHeight: '-webkit-fill-available',
						maxHeight: '-webkit-fill-available',
					},
				}}
			>
				{videoStreamActive && renderPreviewHeader()}
				{videoFinishedRecording && renderRecordedVideoHeader()}

				<ModalBody px={0} pt={0} pb={0} flex={1} display="flex" flexDirection="column" overflow="hidden">
					<Box display="flex" flexDirection="column" position="relative" flex={1}>
						<Box
							flex="1"
							borderTopRadius="xl"
							borderBottomRadius={videoFinishedRecording ? 'xl' : 'none'}
							mx={videoFinishedRecording ? 2 : 0}
							mb={videoFinishedRecording ? 2 : 0}
							mt={videoFinishedRecording ? 1 : 0}
							overflow="hidden"
							position="relative"
							height="100%"
							backgroundColor="black"
						>
							<Box
								position="absolute"
								top="0"
								left="0"
								width="100%"
								height="100%"
								display="flex"
								alignItems="center"
								justifyContent="center"
								opacity={showLoadingSkeleton ? 1 : 0}
								transition="opacity 0.3s ease-in-out"
								pointerEvents="none"
							>
								<Skeleton position="absolute" top="0" left="0" width="100%" height="100%" startColor="#000000" endColor="#000000" speed={0} opacity={1} />
								<Text color="white" zIndex={1}>
									{t('modal.camera_starting')}
								</Text>
							</Box>
							<Box opacity={showLoadingSkeleton ? 0 : 1} transition="opacity 0.1s ease-in-out">
								<Box
									as="video"
									ref={videoRef}
									data-testid="live-preview-video"
									disablePictureInPicture
									controlsList="nodownload nofullscreen noremoteplayback"
									disableRemotePlayback
									playsInline
									autoPlay
									display={!recordedPreviewReady ? 'block' : 'none'}
									objectFit="cover"
									objectPosition="center 100%"
									position="absolute"
									top="0"
									left="0"
									width="100%"
									height="100%"
									muted
								/>
								<CustomVideoPlayer
									videoId="recorded-preview"
									title="Recorded Video"
									poster={videoPoster}
									ref={recordedVideoRef}
									className="recorded-preview-video"
									data-testid="recorded-preview-video"
									defaultMuted={false}
									disableControls={showRecordButton}
									resetOnSourceChange={true}
									hideControlsOnMouseLeave={false}
									objectFit="cover"
									containerProps={{
										position: 'unset',
										display: recordedPreviewReady ? 'block' : 'none',
									}}
								>
									{recordedVideo && <source src={recordedVideo} type={`video/${getVideoFilename().split('.').pop()}`} />}
								</CustomVideoPlayer>
							</Box>
						</Box>

						{countdown !== null && <CountdownOverlay countdown={countdown} />}
						{showRecordButton && (
							<>
								<RecordButton data-testid="record-button" onStartRecording={onStartRecording} />
								<Box position="absolute" right="24px" bottom="32px">
									<DeviceSettings
										selectedVideoDeviceId={selectedVideoDeviceRef.current || ''}
										selectedAudioDeviceId={selectedAudioDeviceRef.current || ''}
										devices={devices}
										onDeviceChange={handleDeviceChange}
									/>
								</Box>
							</>
						)}
						{isRecording && (
							<>
								<MobileResetVideoButton onRestartRecording={onRestartRecording} />
								<StopButton data-testid="stop-button" onStopRecording={onStopRecording} />
							</>
						)}
					</Box>
				</ModalBody>

				{videoFinishedRecording && (
					<Box
						borderTop="1px solid"
						borderColor="border.default"
						p={4}
						bg="white"
						sx={{
							'@supports (-webkit-touch-callout: none)': {
								paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
							},
						}}
					>
						{mobileVideoFeatureFlag ? (
							<Flex mb={3} justifyContent="space-between" alignItems="center" gap={2}>
								<Flex gap={2} alignItems="center">
									<Tag variant="subtle" colorScheme="purple" size="sm" borderRadius="full" py={rem(5)} px={rem(8)} fontWeight={'semibold'}>
										<SparklesSolid size="xs" />
										<Box as="span" textTransform="capitalize">
											{t('beta')}
										</Box>
									</Tag>
									<Text fontSize="sm" fontWeight="medium" color={aiProcessingEnabled ? 'text.default' : 'text.muted'}>
										{t('modal.actions.ai_processing')}
									</Text>
								</Flex>
								<Switch isChecked={aiProcessingEnabled} onChange={onToggleAiProcessing} pr={rem(4)} sx={{ transform: 'scale(1.2)' }} />
							</Flex>
						) : (
							<Flex />
						)}
						<Flex gap={3} justifyContent="space-between">
							<Button variant="ghost" pl={0} colorScheme="secondary" leftIcon={<RefreshOutline />} onClick={onRestartRecording} width="35%">
								{restartButton}
							</Button>
							<Button colorScheme="primary" onClick={onSubmit} width="65%" isLoading={isLoadingSuggestedReason} loadingText={t('modal.processing')}>
								{submitButton}
							</Button>
						</Flex>
					</Box>
				)}
			</ModalContent>
			<GiveModalConfirmDelete isOpen={confirmModalIsOpen} onClose={confirmModalOnClose} onConfirm={onConfirmModalClose} />
		</Modal>
	);
};
